/**
 * International Telephone Input Setup
 * Initializes intl-tel-input for phone number fields with country flags
 */

import intlTelInput from 'intl-tel-input';
import safeDynamicImport from 'common/safe_dynamic_import';
import 'intl-tel-input/build/css/intlTelInput.css';

/**
 * Initialize intl-tel-input on all phone inputs
 * Supports both input[type="tel"] and input.intl-tel-input
 */
export function initializeIntlTelInput() {
  const phoneInputs = document.querySelectorAll('input[type="tel"], input.intl-tel-input');

  phoneInputs.forEach(input => {
    // Skip if already initialized
    if (input.dataset.intlTelInputInitialized === 'true') {
      return;
    }

    try {
      // Default based on locale: en-CA -> Canada, en-US or default -> USA
      const lang = (document.documentElement.getAttribute('lang') || 'en-US').toLowerCase();
      const initialCountry = lang.includes('en-ca') ? 'ca' : 'us';

      const iti = intlTelInput(input, {
        initialCountry,
        nationalMode: true, // Display in national format
        separateDialCode: false, // Don't show dial code separately
        preferredCountries: ['us', 'ca'],
        allowDropdown: true,
        showFlags: true,
        autoPlaceholder: 'off',
        formatOnDisplay: true,
        loadUtils: () => safeDynamicImport(
          () => import(/* webpackChunkName: "intl_tel_input_utils" */ 'intl-tel-input/build/js/utils.js'),
          'reload_once_iti_utils'
        )
      });
      
      // Format the phone number in national format when utils are loaded
      iti.promise.then(() => {
        const number = iti.getNumber();
        if (number && window.intlTelInputUtils) {
          // Format as national (e.g., (847) 550-2400)
          const formatted = window.intlTelInputUtils.formatNumber(
            number, 
            iti.getSelectedCountryData().iso2, 
            window.intlTelInputUtils.numberFormat.NATIONAL
          );
          input.value = formatted;
        }
      }).catch(() => {});

      // Handle Bootstrap form-floating compatibility
      const formFloating = input.closest('.form-floating');
      if (formFloating) {
        const itiContainer = input.parentElement;
        if (itiContainer?.classList.contains('iti')) {
          itiContainer.style.width = '100%';
          itiContainer.style.height = '100%';
        }
      }

      // Mark as initialized
      input.dataset.intlTelInputInitialized = 'true';
    } catch (error) {
      console.error('[intl-tel-input] Failed to initialize:', error);
    }
  });
}

// Expose globally for manual initialization
window.initializeIntlTelInput = initializeIntlTelInput;

/**
 * Setup auto-initialization on page load and Turbo navigation
 */
export function setupIntlTelInputListeners() {
  document.addEventListener('DOMContentLoaded', initializeIntlTelInput);
  document.addEventListener('turbo:load', initializeIntlTelInput);
  
  // Fallback for forms that don't trigger turbo:load properly
  setTimeout(initializeIntlTelInput, 1000);
}

